// Pedigree tree building and common ancestor detection

const ANCESTOR_COLOR_PALETTE = [
  [255, 200, 200], // Light red
  [200, 230, 200], // Light green
  [200, 200, 255], // Light blue
  [255, 255, 200], // Light yellow
  [255, 200, 255], // Light purple
  [200, 255, 255], // Light cyan
  [255, 220, 200], // Light orange
  [220, 200, 255], // Light lavender
  [200, 240, 240], // Light teal
  [255, 220, 220], // Light pink
  [220, 255, 200], // Light lime
  [240, 200, 200], // Light salmon
  [200, 200, 220], // Light periwinkle
  [255, 240, 200], // Light peach
  [220, 220, 255], // Light periwinkle blue
  [200, 255, 220]  // Light mint
];

class PedigreeService {
  /**
   * Build a full pedigree tree for a bird up to N generations.
   * @param {Function} getBirdById - async function to fetch a bird by ID
   * @param {number|string} birdId - root bird ID
   * @param {number} generations - depth of pedigree (default 4)
   * @returns {Object} pedigree object with .bird as the root
   */
  static async getPedigree(getBirdById, birdId, generations = 4) {
    const pedigree = {};
    const buildingCache = new Map();

    const getBirdWithAncestors = async (id, generation, path = new Set()) => {
      if (!id || generation > generations) return null;
      if (path.has(id)) return null;

      const cacheKey = `${id}-${generation}`;
      if (buildingCache.has(cacheKey)) {
        return await buildingCache.get(cacheKey);
      }

      const nextPath = new Set(path);
      nextPath.add(id);

      const birdPromise = (async () => {
        const bird = await getBirdById(id);
        if (!bird) return null;

        const birdWithAncestors = { ...bird };

        if (generation < generations) {
          birdWithAncestors.sire = await getBirdWithAncestors(bird.sire_id, generation + 1, nextPath);
          birdWithAncestors.dam = await getBirdWithAncestors(bird.dam_id, generation + 1, nextPath);
        }

        return birdWithAncestors;
      })();

      buildingCache.set(cacheKey, birdPromise);
      return await birdPromise;
    };

    pedigree.bird = await getBirdWithAncestors(birdId, 1);
    return pedigree;
  }

  /**
   * Find common ancestors (birds appearing multiple times) in a pedigree.
   * @param {Function} getBirdById - async function to fetch a bird by ID
   * @param {number|string} birdId - root bird ID
   * @param {number} generations - depth to search (default 5)
   * @returns {Object} { duplicates: Map, duplicateGroups: Array, allDuplicateIds: Array }
   */
  static async findCommonAncestors(getBirdById, birdId, generations = 5) {
    const pedigree = await this.getPedigree(getBirdById, birdId, generations);
    const ancestors = new Map();
    const duplicates = [];

    const collectAncestors = (bird, path = '', isMainBird = false) => {
      if (!bird) return;

      const key = bird.id;

      if (!isMainBird) {
        if (ancestors.has(key)) {
          const existing = ancestors.get(key);
          const duplicateGroup = duplicates.find(group => group.id === key);

          if (duplicateGroup) {
            duplicateGroup.occurrences.push({ bird, path });
          } else {
            duplicates.push({
              id: key,
              bird,
              occurrences: [
                { bird: existing.bird, path: existing.path },
                { bird, path }
              ]
            });
          }
        } else {
          ancestors.set(key, { bird, path });
        }
      }

      if (bird.sire) collectAncestors(bird.sire, path + 'S', false);
      if (bird.dam) collectAncestors(bird.dam, path + 'D', false);
    };

    collectAncestors(pedigree.bird, '', true);

    const colorMap = new Map();
    duplicates.forEach((group, index) => {
      const color = ANCESTOR_COLOR_PALETTE[index % ANCESTOR_COLOR_PALETTE.length];
      colorMap.set(group.id, {
        color: `rgb(${color[0]}, ${color[1]}, ${color[2]})`,
        // occurrences already contains every appearance (the first one is
        // added when the duplicate group is created)
        occurrences: group.occurrences.length
      });
    });

    return {
      duplicates: colorMap,
      duplicateGroups: duplicates,
      allDuplicateIds: Array.from(new Set(duplicates.map(g => g.id)))
    };
  }
}

export default PedigreeService;
